<template>
  <main class="login-page">
    <section class="login-shell" aria-labelledby="login-title">
      <aside class="brand-panel" aria-label="耘图AI同创产品介绍">
        <div class="login-brand">
          <img src="@/assets/yuntu-logo.jpg" alt="耘图AI同创" />
          <div><strong>耘图AI同创</strong><span>AI · DESIGN STUDIO</span></div>
        </div>
        <div class="brand-copy">
          <span class="brand-kicker">设计与类设计行业 AI 超级助手</span>
          <h2>让每一个设计灵感<br />更快成为作品</h2>
          <p>从构思、改造到高清呈现，用 AI 连接你的完整创作流程。</p>
        </div>
        <div class="visual-stage" aria-hidden="true">
          <div class="material-card material-card-main">
            <span class="material-label">LIVING SPACE</span>
            <div class="room-art">
              <i class="room-window"></i><i class="room-sofa"></i><i class="room-table"></i>
            </div>
          </div>
          <div class="material-card material-card-small"><i class="el-icon-magic-stick"></i><span>AI 智能生成</span></div>
        </div>
        <div class="brand-points">
          <span><i class="el-icon-check"></i> 专业设计工具</span>
          <span><i class="el-icon-check"></i> 高效创作流程</span>
        </div>
      </aside>

      <div class="login-card">
        <div class="mobile-brand">
          <img src="@/assets/yuntu-logo.jpg" alt="" />
          <strong>耘图AI同创</strong>
        </div>

        <header class="form-heading">
          <span class="welcome-mark">{{ mode === "login" ? "WELCOME BACK" : "JOIN YUNTU AI" }}</span>
          <h1 id="login-title">{{ mode === "login" ? "欢迎回来" : "创建账号" }}</h1>
          <p>{{ mode === "login" ? "登录后继续你的创作旅程" : "注册账号，开启 AI 设计新体验" }}</p>
        </header>

      <el-form
        ref="authForm"
        :model="form"
        :rules="rules"
        autocomplete="on"
        @submit.native.prevent="submit"
      >
        <el-form-item v-if="mode === 'register'" prop="displayName">
          <label>用户名</label>
          <el-input
            v-model.trim="form.displayName"
            type="text"
            autocomplete="name"
            maxlength="80"
            placeholder="请输入您的姓名或公司名"
            prefix-icon="el-icon-office-building"
            @keyup.enter.native="submit"
          />
        </el-form-item>
        <el-form-item prop="username">
          <label>{{ mode === "register" ? "手机号" : "账号或手机号" }}</label>
          <el-input
            v-model.trim="form.username"
            name="username"
            :type="mode === 'register' ? 'tel' : 'text'"
            autocomplete="username"
            :maxlength="mode === 'register' ? 11 : 64"
            :placeholder="mode === 'register' ? '请输入手机号' : '请输入账号或手机号'"
            prefix-icon="el-icon-user"
            @keyup.enter.native="submit"
          />
        </el-form-item>
        <el-form-item prop="password">
          <label>密码</label>
          <el-input
            v-model="form.password"
            name="password"
            type="password"
            show-password
            :autocomplete="
              mode === 'register' ? 'new-password' : 'current-password'
            "
            :placeholder="
              mode === 'register' ? '请输入 6–32 位密码' : '请输入密码'
            "
            maxlength="32"
            prefix-icon="el-icon-lock"
            @keyup.enter.native="submit"
          />
        </el-form-item>
        <div v-if="mode === 'login'" class="login-options">
          <el-checkbox
            v-model="rememberCredentials"
            @change="handleRememberChange"
          >记住密码</el-checkbox>
          <span class="remember-tip">仅建议在个人设备上使用</span>
        </div>
        <el-form-item v-if="mode === 'register'" prop="confirmPassword">
          <label>确认密码</label>
          <el-input
            v-model="form.confirmPassword"
            type="password"
            show-password
            autocomplete="new-password"
            placeholder="请再次输入密码"
            maxlength="32"
            prefix-icon="el-icon-lock"
            @keyup.enter.native="submit"
          />
        </el-form-item>
        <p v-if="errorMessage" class="login-error" role="alert">
          {{ errorMessage }}
        </p>
        <el-button
          class="login-submit"
          type="primary"
          native-type="submit"
          :loading="loading"
        >
          {{ submitText }}
        </el-button>
        <div class="auth-switch-row" :class="{ 'register-invite': mode === 'login' }">
          <span>{{ mode === "login" ? "还没有账号？" : "已经有账号？" }}</span>
          <button
            type="button"
            class="auth-switch-button"
            @click="switchMode(mode === 'login' ? 'register' : 'login')"
          >
            {{ mode === "login" ? "注册" : "返回登录" }}
          </button>
        </div>
      </el-form>
      </div>
    </section>
    <footer class="login-footer">
      <div class="login-footer-content">
        <img
          class="login-footer-badge"
          src="@/assets/beian-badge.png"
          alt="备案标识"
        />
        <div class="login-footer-copy">
          <p>Copyright © 2026. All Rights Reserved. · 京ICP备2025155765号</p>
        </div>
      </div>
    </footer>
  </main>
</template>

<script>
import { authState, login, register } from "@/services/auth";
import { safeWorkflowReturnUrl } from "@/utils/workflowAppUrl";

const REMEMBERED_CREDENTIALS_COOKIE = "yuntu_remembered_credentials";
const LEGACY_REMEMBERED_CREDENTIALS_KEY = "yuntu.rememberedCredentials";
const REMEMBERED_CREDENTIALS_MAX_AGE = 60 * 60 * 24 * 30;

function readCookie(name) {
  const prefix = `${name}=`;
  const item = document.cookie
    .split(";")
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(prefix));
  return item ? item.slice(prefix.length) : "";
}

function readRememberedCredentials() {
  try {
    const cookieValue = readCookie(REMEMBERED_CREDENTIALS_COOKIE);
    if (!cookieValue) return null;
    const saved = JSON.parse(decodeURIComponent(cookieValue));
    if (
      saved &&
      typeof saved.username === "string" &&
      typeof saved.password === "string"
    ) {
      return saved;
    }
  } catch (error) {
    clearRememberedCredentials();
  }
  return null;
}

function saveRememberedCredentials(username, password) {
  const value = encodeURIComponent(JSON.stringify({ username, password }));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${REMEMBERED_CREDENTIALS_COOKIE}=${value}; Max-Age=${REMEMBERED_CREDENTIALS_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  try {
    localStorage.removeItem(LEGACY_REMEMBERED_CREDENTIALS_KEY);
  } catch (error) {
    // 旧本地存储不可用时，不影响 Cookie 保存。
  }
}

function clearRememberedCredentials() {
  document.cookie = `${REMEMBERED_CREDENTIALS_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
  try {
    localStorage.removeItem(LEGACY_REMEMBERED_CREDENTIALS_KEY);
  } catch (error) {
    // 旧本地存储不可用时，无需阻断登录流程。
  }
}

async function offerPasswordManagerSave(username, password) {
  if (
    !window.PasswordCredential ||
    !navigator.credentials ||
    typeof navigator.credentials.store !== "function"
  ) {
    return;
  }
  try {
    const credential = new window.PasswordCredential({
      id: username,
      name: username,
      password,
    });
    await navigator.credentials.store(credential);
  } catch (error) {
    // 浏览器未开启密码管理、用户拒绝或当前环境不支持时，不影响登录。
  }
}

export default {
  name: "LoginPage",
  data() {
    return {
      loading: false,
      mode: "login",
      errorMessage: authState.sessionError,
      rememberCredentials: false,
      form: { displayName: "", username: "", password: "", confirmPassword: "" },
    };
  },
  computed: {
    rules() {
      const passwordRules = [
        { required: true, message: "请输入密码", trigger: "blur" },
      ];
      if (this.mode === "register") {
        passwordRules.push({
          min: 6,
          max: 32,
          message: "密码长度应为 6–32 位",
          trigger: "blur",
        });
      }
      return {
        displayName:
          this.mode === "register"
            ? [
                { required: true, message: "请输入用户名", trigger: "blur" },
                { max: 80, message: "用户名不能超过 80 个字符", trigger: "blur" },
              ]
            : [],
        username:
          this.mode === "register"
            ? [
                {
                  required: true,
                  message: "请输入手机号",
                  trigger: "blur",
                },
                {
                  pattern: /^1[3-9]\d{9}$/,
                  message: "请输入正确的 11 位手机号",
                  trigger: "blur",
                },
              ]
            : [
                {
                  required: true,
                  message: "请输入账号或手机号",
                  trigger: "blur",
                },
              ],
        password: passwordRules,
        confirmPassword:
          this.mode === "register"
            ? [
                { required: true, message: "请再次输入密码", trigger: "blur" },
                {
                  validator: (rule, value, callback) => {
                    if (value !== this.form.password)
                      callback(new Error("两次输入的密码不一致"));
                    else callback();
                  },
                  trigger: ["blur", "change"],
                },
              ]
            : [],
      };
    },
    submitText() {
      if (this.loading)
        return this.mode === "register" ? "正在注册" : "正在登录";
      return this.mode === "register" ? "注册并登录" : "登录";
    },
  },
  created() {
    if (this.$route.query.code || this.$route.query.mode === 'register') this.mode = 'register';
    const remembered = this.mode === "login" ? readRememberedCredentials() : null;
    if (remembered) {
      this.form.username = remembered.username;
      this.form.password = remembered.password;
      this.rememberCredentials = true;
    }
  },
  methods: {
    handleRememberChange(checked) {
      if (!checked) clearRememberedCredentials();
    },
    switchMode(mode) {
      if (this.loading || this.mode === mode) return;
      this.mode = mode;
      this.errorMessage = "";
      const remembered = mode === "login" ? readRememberedCredentials() : null;
      this.form.username = remembered ? remembered.username : this.form.username;
      this.form.password = remembered ? remembered.password : "";
      this.rememberCredentials = Boolean(remembered);
      this.form.confirmPassword = "";
      this.$nextTick(
        () => this.$refs.authForm && this.$refs.authForm.clearValidate(),
      );
    },
    submit() {
      this.$refs.authForm.validate(async (valid) => {
        if (!valid || this.loading) return;
        this.loading = true;
        this.errorMessage = "";
        try {
          if (this.mode === "register") {
            await register(
              this.form.username,
              this.form.password,
              this.form.confirmPassword,
              this.form.displayName,
              typeof this.$route.query.code === 'string' ? this.$route.query.code : '',
            );
          } else {
            await login(this.form.username, this.form.password);
            await offerPasswordManagerSave(
              this.form.username,
              this.form.password,
            );
            if (this.rememberCredentials) {
              saveRememberedCredentials(this.form.username, this.form.password);
            } else {
              clearRememberedCredentials();
            }
          }
          this.form.password = "";
          this.form.confirmPassword = "";
          const workflowReturn = safeWorkflowReturnUrl(this.$route.query.returnTo);
          if (workflowReturn) {
            window.location.replace(workflowReturn);
            return;
          }
          const redirect =
            typeof this.$route.query.redirect === "string" &&
            this.$route.query.redirect.startsWith("/") &&
            !this.$route.query.redirect.startsWith("//")
              ? this.$route.query.redirect
              : "/home";
          await this.$router.replace(redirect);
        } catch (error) {
          this.errorMessage =
            error.message ||
            (this.mode === "register"
              ? "注册失败，请重试"
              : "登录失败，请重试");
        } finally {
          this.loading = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.login-page {
  width: 100%;
  min-width: 0;
  min-height: 100vh;
  min-height: 100dvh;
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden auto;
  padding: max(28px, env(safe-area-inset-top))
    max(20px, env(safe-area-inset-right))
    max(82px, calc(env(safe-area-inset-bottom) + 70px))
    max(20px, env(safe-area-inset-left));
  background: radial-gradient(circle at 12% 10%, rgba(183, 84, 46, 0.12), transparent 30%),
    radial-gradient(circle at 88% 92%, rgba(179, 117, 79, 0.1), transparent 28%),
    linear-gradient(145deg, #f3eee8 0%, #fbfaf8 52%, #eee8e1 100%);
}
.login-page::before,
.login-page::after {
  content: "";
  position: absolute;
  z-index: -1;
  border: 1px solid rgba(127, 82, 55, 0.1);
  border-radius: 50%;
  pointer-events: none;
}
.login-page::before {
  width: 360px;
  height: 360px;
  top: -190px;
  right: -90px;
}
.login-page::after {
  width: 260px;
  height: 260px;
  bottom: 24px;
  left: -150px;
}
.login-footer {
  position: absolute;
  right: 20px;
  bottom: max(12px, env(safe-area-inset-bottom));
  left: 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #526174;
  text-align: center;
  font-size: 10px;
  line-height: 20px;
}
.login-footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.login-footer-badge {
  width: 16px;
  height: auto;
  flex: 0 0 auto;
  object-fit: contain;
}
.login-footer-copy {
  min-width: 0;
}
.login-footer p {
  margin: 0;
}
.login-footer-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  column-gap: 16px;
  row-gap: 2px;
}
.login-footer-meta span,
.login-footer-meta a {
  white-space: nowrap;
}
.login-footer a {
  color: #526174;
  text-decoration: none;
  transition: color 0.18s ease;
}
.login-footer a:hover {
  color: #2458cc;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.login-footer a:focus-visible {
  outline: 3px solid rgba(82, 120, 219, 0.18);
  outline-offset: 3px;
}
.login-shell {
  width: min(980px, 100%);
  min-height: 610px;
  flex: 0 0 auto;
  margin: auto;
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(390px, 0.92fr);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 34px 90px rgba(70, 47, 34, 0.16), 0 3px 12px rgba(70, 47, 34, 0.05);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}
.brand-panel {
  min-width: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 42px 46px 36px;
  color: #fff;
  background: linear-gradient(148deg, #742817 0%, #9e4127 48%, #bd6b42 100%);
}
.brand-panel::before { content: ""; position: absolute; inset: 0; background: radial-gradient(circle at 80% 12%, rgba(255,255,255,.18), transparent 24%), linear-gradient(115deg, transparent 35%, rgba(255,255,255,.06)); pointer-events: none; }
.login-brand {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 13px;
  margin-bottom: 44px;
  text-align: left;
}
.login-brand img {
  width: 46px;
  height: 46px;
  border-radius: 6px;
  object-fit: cover;
  box-shadow: 0 8px 20px rgba(49, 13, 5, 0.22);
}
.login-brand div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.login-brand strong { color: #fff; font-size: 18px; letter-spacing: .02em; }
.login-brand span {
  color: rgba(255,255,255,.68);
  font-size: 9px;
  letter-spacing: 0.18em;
}
.brand-copy { position: relative; z-index: 1; }
.brand-kicker { display: inline-flex; align-items: center; min-height: 26px; padding: 0 11px; border: 1px solid rgba(255,255,255,.22); border-radius: 5px; background: rgba(255,255,255,.08); color: rgba(255,255,255,.86); font-size: 11px; letter-spacing: .12em; }
.brand-copy h2 { margin: 18px 0 12px; font-size: clamp(29px, 3vw, 38px); line-height: 1.32; letter-spacing: -.02em; }
.brand-copy p { max-width: 370px; margin: 0; color: rgba(255,255,255,.72); font-size: 13px; line-height: 1.8; }
.visual-stage { min-height: 230px; position: relative; flex: 1; margin: 16px -4px 0; }
.material-card { position: absolute; border: 1px solid rgba(255,255,255,.23); background: rgba(255,255,255,.12); box-shadow: 0 18px 38px rgba(53,13,5,.2); backdrop-filter: blur(10px); }
.material-card-main { width: 280px; height: 175px; top: 26px; left: 22px; overflow: hidden; border-radius: 10px; transform: rotate(-2deg); }
.material-label { position: absolute; z-index: 2; top: 13px; left: 16px; font-size: 9px; letter-spacing: .16em; color: rgba(255,255,255,.75); }
.room-art { position: absolute; inset: 0; background: linear-gradient(145deg,#ddb994,#b87958); }
.room-art::before { content:""; position:absolute; inset:0; background:linear-gradient(118deg,rgba(255,255,255,.22),transparent 42%),linear-gradient(to top,rgba(82,35,21,.22),transparent 45%); }
.room-window { position:absolute; width:78px; height:82px; top:31px; right:25px; border:5px solid rgba(255,244,226,.72); background:linear-gradient(#b7d0d2,#e6d5b8); box-shadow:0 0 24px rgba(255,239,201,.35); }
.room-sofa { position:absolute; width:132px; height:47px; left:25px; bottom:27px; border-radius:8px 8px 3px 3px; background:#efe2cc; box-shadow:inset 0 7px rgba(255,255,255,.25),0 10px 18px rgba(68,30,19,.16); }
.room-table { position:absolute; width:60px; height:24px; right:46px; bottom:21px; border-radius:50%; background:#6d3c2e; box-shadow:0 10px 0 -8px #4b2b22; }
.material-card-small { right:8px; bottom:16px; display:flex; align-items:center; gap:8px; min-height:44px; padding:0 14px; border-radius:6px; font-size:11px; transform:rotate(3deg); }
.material-card-small i { font-size:15px; color:#ffd8a8; }
.brand-points { position:relative; z-index:1; display:flex; gap:20px; color:rgba(255,255,255,.72); font-size:11px; }
.brand-points span { display:flex; align-items:center; gap:6px; }
.brand-points i { display:grid; place-items:center; width:16px; height:16px; border-radius:50%; background:rgba(255,255,255,.14); color:#ffd7b4; font-size:9px; }
.login-card { min-width: 0; display: flex; flex-direction: column; justify-content: center; padding: 50px 52px; background: rgba(255,255,255,.96); }
.mobile-brand { display:none; }
.form-heading { margin-bottom: 28px; }
.welcome-mark { color:#a54b2d; font-size:10px; font-weight:700; letter-spacing:.17em; }
.form-heading h1 { margin:10px 0 7px; color:#211d1b; font-size:28px; line-height:1.25; letter-spacing:-.02em; }
.form-heading p { margin:0; color:#908985; font-size:13px; }
.auth-switch-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-top: 18px;
  color: #8a96a9;
  font-size: 12px;
}
.auth-switch-button {
  min-width: 44px;
  min-height: 44px;
  border: 0;
  border-radius: 6px;
  padding: 3px 7px;
  background: transparent;
  color: #a3482c;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.18s ease, background-color 0.18s ease;
}
.auth-switch-button:hover {
  background: #fff2ec;
  color: #88371f;
}
.auth-switch-button:focus-visible {
  outline: 3px solid rgba(168, 72, 43, 0.18);
  outline-offset: 2px;
}
.auth-switch-row.register-invite {
  margin-top: 22px;
  border-top: 1px solid #edf0f5;
  padding-top: 16px;
  color: #8a96a9;
}
.register-invite .auth-switch-button {
  min-width: 44px;
  border: 0;
  background: transparent;
  color: #a3482c;
  font-weight: 600;
}
.register-invite .auth-switch-button:hover {
  background: #fff5f0;
  color: #88371f;
}
.login-card label {
  display: block;
  margin-bottom: 7px;
  color: #4a403c;
  font-size: 12px;
  font-weight: 600;
}
.login-card ::v-deep .el-input__inner {
  height: 46px;
  border-color: #e5ddd8;
  border-radius: 5px;
  background: #fbf9f7;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.login-card ::v-deep .el-input__inner:hover {
  border-color: #cdb5a9;
}
.login-card ::v-deep .el-input__inner:focus {
  border-color: #ad5233;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(173, 82, 51, 0.12);
}
.login-card ::v-deep .el-input__prefix {
  color: #a69a94;
}
.login-card ::v-deep .el-form-item {
  margin-bottom: 22px;
}
.login-options {
  min-height: 28px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: -8px 0 16px;
}
.login-options ::v-deep .el-checkbox {
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  margin: 0;
}
.login-options ::v-deep .el-checkbox__label {
  padding-left: 7px;
  color: #5e5652;
  font-size: 12px;
}
.login-options ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #a3482c;
  background-color: #a3482c;
}
.login-options ::v-deep .el-checkbox__input.is-focus .el-checkbox__inner {
  box-shadow: 0 0 0 3px rgba(163, 72, 44, 0.14);
}
.remember-tip {
  color: #9a918c;
  font-size: 11px;
  line-height: 28px;
  white-space: nowrap;
}
.field-helper {
  margin: 6px 0 0;
  color: #7b8799;
  font-size: 12px;
  line-height: 1.5;
}
.login-error {
  margin: -4px 0 16px;
  color: #e05252;
  font-size: 12px;
}
.login-submit {
  width: 100%;
  height: 46px;
  margin-top: 4px;
  border: 0;
  border-radius: 5px;
  background: linear-gradient(135deg, #8c331e, #b55232);
  font-weight: 600;
  box-shadow: 0 10px 22px rgba(153, 59, 32, 0.23);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.login-submit:hover,
.login-submit:focus {
  background: linear-gradient(135deg, #7c2b19, #a8482c);
  transform: translateY(-1px);
  box-shadow: 0 13px 25px rgba(153, 59, 32, 0.28);
}
.login-submit:active {
  transform: translateY(0);
}
@media (max-width: 820px) {
  .login-shell { width:min(460px,100%); min-height:0; display:block; border-radius:14px; }
  .brand-panel { display:none; }
  .login-card { padding:38px 40px 42px; }
  .mobile-brand { display:flex; align-items:center; gap:10px; margin-bottom:30px; color:#2c2421; font-size:16px; }
  .mobile-brand img { width:40px; height:40px; border-radius:5px; object-fit:cover; box-shadow:0 7px 16px rgba(92,43,27,.16); }
}
@media (max-width: 520px) {
  .login-page {
    align-items: stretch;
    justify-content: flex-start;
    flex-direction: column;
    padding-right: 16px;
    padding-bottom: max(24px, calc(env(safe-area-inset-bottom) + 20px));
    padding-left: 16px;
  }
  .login-footer {
    width: 100%;
    position: static;
    margin-top: auto;
    padding-top: 24px;
    box-sizing: border-box;
  }
  .login-footer-content {
    align-items: flex-start;
    gap: 8px;
  }
  .login-footer-badge {
    width: 22px;
    margin-top: 5px;
  }
  .login-shell { width: 100%; margin: 0 auto; border-radius:12px; }
  .login-card { padding:30px 24px 34px; }
  .form-heading { margin-bottom:24px; }
  .form-heading h1 { font-size:25px; }
  .login-options { align-items:flex-start; }
  .remember-tip { max-width:130px; line-height:1.45; text-align:right; white-space:normal; }
}
@media (max-height: 640px) {
  .login-page {
    align-items: flex-start;
  }
  .login-card {
    margin-top: auto;
    margin-bottom: auto;
    padding-top: 28px;
    padding-bottom: 30px;
  }
  .mobile-brand { margin-bottom:24px; }
  .login-card ::v-deep .el-form-item {
    margin-bottom: 18px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .login-card ::v-deep .el-input__inner,
  .login-submit,
  .auth-switch-button,
  .login-footer a {
    transition: none;
  }
  .material-card { transform:none; }
}
</style>
